
import React, { useEffect, useRef, useState } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import { ActionSection, FieldWrappers, InputGroup, Label, ModalHeader, ScrollableContent, SideCancelBtn, SubmitBtn, Wrapper } from "../../Styles/InputStyles";
import SingleFlatSelectModal from "../../SingleFlatSelectModal";
import CategoryCard from "../../CategoryCard";

const WifiPasswordModal = ({
    isWifiPassword,
    setIsWifiPassword,
    handleDetailModalClose,
    wifiDetailsMutate,
    wifiDetailsLoading,
    selectedData
}) => {

    const [height, setHeight] = useState(window.innerHeight);
    const formikRef = useRef(null);
    const [showFlatSelectModal, setShowFlatSelectModal] = useState(false);
    const [selectedFlat, setSelectedFlat] = useState(null);

    const hasFlats = selectedData?.flats && selectedData.flats.length > 0;

    const flatOptions = hasFlats ? selectedData.flats.map(flat => ({
        id: flat.id,
        name: flat.name || `Flat ${flat.id}`,
        wifiPassword: flat.wifiPassword || ""
    })) : [];

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const getInitialWifiPassword = () => {
        if (hasFlats && selectedFlat) {
            return selectedFlat.wifiPassword || "";
        }
        return selectedData?.wifiPassword || "";
    };

    const initialValues = {
        wifiPassword: getInitialWifiPassword(),
        flatId: selectedFlat?.id || "",
        flatName: selectedFlat?.name || "",
    };

    const validationSchema = Yup.object().shape({
        wifiPassword: Yup.string()
            .min(4, "Wi-Fi password must be at least 4 characters long")
            .max(30, "Wi-Fi password must not exceed 30 characters")
            .required("Wi-Fi password is required"),
        flatId: hasFlats
            ? Yup.string().required("Please select a flat")
            : Yup.string().notRequired(),
    });

    const handleSubmit = (values) => {
        const payload = {
            wifiPassword: values.wifiPassword,
            propId: selectedData.id,
        };

        if (hasFlats && values.flatId) {
            payload.flatId = values.flatId;
        }

        wifiDetailsMutate(payload);

    };

    const handleFlatSelect = (flat) => {
        setSelectedFlat(flat);
        if (formikRef.current) {
            formikRef.current.setFieldValue('flatId', flat?.id || '');
            formikRef.current.setFieldValue('flatName', flat?.name || '');
            formikRef.current.setFieldValue('wifiPassword', flat?.wifiPassword || '');
            formikRef.current.setFieldError('flatId', '');
        }
        setShowFlatSelectModal(false);
    };

    useEffect(() => {
        if (!isWifiPassword) {
            setSelectedFlat(null);
            if (formikRef.current) {
                formikRef.current.resetForm();
            }
        }
    }, [isWifiPassword]);

    useEffect(() => {
        if (formikRef.current && selectedFlat) {
            formikRef.current.setFieldValue('wifiPassword', selectedFlat.wifiPassword || '');
        }
    }, [selectedFlat]);

    const selectedCategory = selectedFlat ? {
        label: `Flat ${selectedFlat.name}`,
        value: selectedFlat.id
    } : null;

    return (
        <Container style={{ height: `${height}px`, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
            <ModalHeader>
                <h1 className="text-lg font-medium text-white">Wi-Fi Password</h1>
                <button onClick={handleDetailModalClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            <div className="custom-scrollbar flex h-full flex-col justify-between px-6">

                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                    validateOnChange={true}
                    validateOnBlur={true}
                    context={{ hasFlats }}
                    enableReinitialize={true}
                >
                    {({ values, setFieldValue, errors, touched, setFieldError }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100vh-140px)]">
                                <Wrapper>
                                    {/* Flat Selection - Only show if flats exist */}
                                    {hasFlats && (
                                        <InputGroup >
                                            <Label >
                                                Select Flat *
                                            </Label>

                                            <CategoryCard
                                                type="button"
                                                selectedCategory={selectedCategory}
                                                title="Select Flat"
                                                onClick={() => setShowFlatSelectModal(true)}
                                            />

                                            {errors.flatId && touched.flatId && (
                                                <div className="mt-1 text-xs text-red-500">
                                                    {errors.flatId}
                                                </div>
                                            )}

                                        </InputGroup>
                                    )}

                                    {/* WiFi Password Input */}
                                    <InputGroup >
                                        <Label >
                                            Wi-Fi Password *
                                        </Label>
                                        <FieldWrappers>
                                            <Field
                                                type="text"
                                                name="wifiPassword"
                                                placeholder="Enter Wi-Fi password"
                                                className="w-full"
                                            />
                                        </FieldWrappers>
                                        {errors.wifiPassword && touched.wifiPassword && (
                                            <div className="mt-1 text-xs text-red-500">
                                                {errors.wifiPassword}
                                            </div>
                                        )}

                                    </InputGroup>


                                </Wrapper>
                            </ScrollableContent>

                            <ActionSection>
                                <SideCancelBtn
                                    type="button"
                                    onClick={() => {
                                        handleDetailModalClose();
                                    }}
                                >
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn
                                    type="submit"
                                    disabled={wifiDetailsLoading}
                                >
                                    {wifiDetailsLoading ? "Submitting..." : "Submit"}
                                </SubmitBtn>
                            </ActionSection>
                        </Form>
                    )}
                </Formik>
            </div>

            {/* Flat Selection Modal */}
            <SingleFlatSelectModal
                isOpen={showFlatSelectModal}
                onClose={() => setShowFlatSelectModal(false)}
                onSelect={handleFlatSelect}
                options={flatOptions}
                selectedFlat={selectedFlat}
                isLoading={false}
                title="Select Flat"
            />
        </Container>
    );
};


const Container = tw.div`bg-white w-full h-full flex flex-col relative`


export default WifiPasswordModal;